<template>
  <div class="catalog-path-rule" @click="modals.open = true">
    <div class="path-presenter-wrapper" :key="localModel">
      <PathPresenter v-model="vm.selectedPath" />
    </div>
    <CatalogNodePathSelectionModal
      v-model="modals.open"
      @confirm="onConfirm"
      @hide="onHideHandler"
      :selected-entry-id="localModel"
      :selected-path="selectedPath" />
  </div>
</template>

<script setup lang="ts">
import CatalogNodePathSelectionModal from "@common/components/MultiLevelNavigation/CatalogNodePathSelectionModal.vue";
import PathPresenter from "@common/components/PathPresenter.vue";
import { useContentLanguages } from "@common/composable/useMetaDataStore";
import { CatalogNodePath } from "@medapro-types/ContentEditor/content-editor-store";
import { Branch, MultiLevelNavigationConfirmModel } from "@medapro-types/common/components/MultiLevelNavigation";
import { PathPresentedDataModel } from "@medapro-types/common/components/PathPresenter";
import catalogNodesApi from "@services/catalogNodes.js";
import _ from "lodash";
import { computed, nextTick, onMounted, reactive } from "vue";

//#region General
const props = defineProps<{ value: string }>();
const emits = defineEmits(["input", "deleteRule"]);
//#endregion

//#region Store
const { primaryLanguageCode } = useContentLanguages();
//#endregion

//#region Viewmodel
type ViewModel = { selectedPath: PathPresentedDataModel[] };
const vm = reactive<ViewModel>({ selectedPath: [] });

const modals = reactive({ open: false });
//#endregion

const localModel = computed({
  get() {
    return props.value;
  },
  set(nv) {
    emits("input", nv);
  }
});
//#endregion

//#region SelectedPath
const selectedPath = computed<Branch[]>(() =>
  vm.selectedPath?.filter((x) => x.id != localModel.value).map((x, index) => ({ id: x.id, depth: index, name: x.name, parentId: null }))
);
//#endregion

//#region EventHandler
const onHideHandler = () => {
  nextTick(() => {
    if (_.isEmpty(localModel.value)) emits("deleteRule");
  });
};

const onConfirm = async (data: MultiLevelNavigationConfirmModel) => {
  if (data.selection && data.selection.id) {
    localModel.value = data.selection.id;
    fetchAndSetPath(data.selection.id);
  }
};

const fetchAndSetPath = async (id: string) => {
  const fetchedPath: CatalogNodePath = await catalogNodesApi.getCatalogNodePathInBaseCatalog(id, primaryLanguageCode.value);
  const mappedPath: PathPresentedDataModel[] = fetchedPath.map((x) => ({ id: x.nodeId, name: x.name }));
  mappedPath.unshift({ id: "", name: "", icon: "Home" });
  vm.selectedPath = mappedPath;
};
//#endregion

//#region LifeCycle
onMounted(() => {
  if (!props.value) {
    modals.open = true;
  } else {
    fetchAndSetPath(props.value);
  }
});
//#endregion
</script>
